import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Trophy, Star, Zap, Users, Award, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function ShakersSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  // Parallax effects
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  const stats = [
    { icon: Users, value: '25+', label: t('shakers.stat1'), color: '#3b82f6' },
    { icon: Trophy, value: '3', label: t('shakers.stat2'), color: '#f59e0b' },
    { icon: Star, value: '2025', label: t('shakers.stat3'), color: '#8DC21F' },
  ];

  return (
    <section ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      {/* Animated celebration background */}
      <motion.div 
        style={{ y, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <motion.div 
          style={{ rotate }}
          className="absolute top-10 left-10 opacity-10"
        >
          <Sparkles size={80} className="text-[#f59e0b]" />
        </motion.div>
        <div className="absolute bottom-20 right-20 w-72 h-72 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #8DC21F 0%, transparent 70%)' }} />
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <motion.span 
            className="v-tag green mb-6 inline-flex"
            animate={{ 
              boxShadow: [
                '0 0 0px rgba(141,194,31,0.3)',
                '0 0 20px rgba(141,194,31,0.4)',
                '0 0 0px rgba(141,194,31,0.3)',
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {t('shakers.tag')}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t('shakers.h1a')}{' '}
            <motion.span 
              style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
              animate={{ 
                textShadow: [
                  '0 0 0px rgba(141,194,31,0)',
                  '0 0 20px rgba(141,194,31,0.3)',
                  '0 0 0px rgba(141,194,31,0)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {t('shakers.h1b')}
            </motion.span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t('shakers.sub')}
          </p>
        </motion.div>

        {/* Stats with trophy bounce animation */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60, scale: 0.8 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: i * 0.2,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -15,
                  scale: 1.05,
                  rotate: [0, -3, 3, 0],
                  transition: { duration: 0.5 }
                }}
                className="liquid-glass rounded-3xl p-8 text-center group cursor-pointer relative overflow-hidden"
              >
                {/* Pulsing background */}
                <motion.div
                  className="absolute inset-0"
                  animate={{ 
                    background: [
                      `radial-gradient(circle at 50% 50%, ${stat.color}00 0%, transparent 70%)`,
                      `radial-gradient(circle at 50% 50%, ${stat.color}15 0%, transparent 70%)`,
                      `radial-gradient(circle at 50% 50%, ${stat.color}00 0%, transparent 70%)`,
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                />

                <motion.div 
                  className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center relative z-10" 
                  style={{ background: `${stat.color}15` }}
                  whileHover={{ 
                    scale: 1.3,
                    rotate: [0, -15, 15, -15, 0],
                    transition: { duration: 0.6 }
                  }}
                >
                  <Icon size={28} style={{ color: stat.color }} />
                </motion.div>
                
                <motion.div 
                  className="text-4xl font-bold mb-2 relative z-10"
                  style={{ color: stat.color }}
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ 
                    duration: 0.6, 
                    delay: i * 0.2 + 0.3,
                    type: "spring",
                    stiffness: 200
                  }}
                >
                  {stat.value}
                </motion.div>
                
                <div className="text-sm text-gray-600 dark:text-white/55 uppercase tracking-wide relative z-10">
                  {stat.label}
                </div>

                {/* Star particles on hover */}
                <motion.div
                  className="absolute top-4 right-4 opacity-0 group-hover:opacity-100"
                  initial={{ scale: 0, rotate: 0 }}
                  whileHover={{ scale: [0, 1.2, 1], rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Award size={16} style={{ color: stat.color }} />
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Criteria card with reveal animation */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          whileHover={{ scale: 1.02 }}
          className="liquid-glass rounded-3xl p-8 sm:p-10 relative overflow-hidden"
        >
          {/* Decorative corner glow */}
          <motion.div 
            className="absolute -top-20 -right-20 w-60 h-60 rounded-full blur-3xl"
            style={{ background: '#8DC21F' }}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.05, 0.15, 0.05]
            }}
            transition={{ duration: 5, repeat: Infinity }}
          />

          <div className="flex items-start gap-4 mb-6 relative z-10">
            <motion.div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" 
              style={{ background: 'rgba(141,194,31,0.15)' }}
              whileHover={{ rotate: 360, scale: 1.2 }}
              transition={{ duration: 0.6 }}
            >
              <Zap size={22} className="text-[#8DC21F]" />
            </motion.div>
            <div>
              <h3 className="text-lg font-bold text-black dark:text-white/90 mb-2">
                {t('shakers.criteriaTitle')}
              </h3>
              <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">
                {t('shakers.criteriaDesc')}
              </p>
            </div>
          </div>
          
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative z-10">
            {Array.isArray(t('shakers.criteria')) && t('shakers.criteria').map((item, i) => (
              <motion.li 
                key={i} 
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
                whileHover={{ x: 5, scale: 1.02 }}
                className="flex items-start gap-2 text-sm text-gray-700 dark:text-white/70"
              >
                <motion.span 
                  className="text-[#8DC21F] mt-0.5"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                >
                  •
                </motion.span>
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
